import { LayoutGrid, Users, Copy, Globe, LogOut, ShieldCheck, Crown, Settings, MapPin, Sparkles, Bell, Heart, MessageCircle, KeyRound } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

// Sidebar navigation grouped into sections and ordered by importance / how
// often each area is used day-to-day.
const SECTIONS = [
  {
    title: "Principal",
    items: [
      { key: "overview", label: "Visão Geral", icon: LayoutGrid },
      { key: "profiles", label: "Perfis", icon: Users },
      { key: "alerts", label: "Alertas", icon: Bell },
      { key: "ai", label: "Pesquisa IA", icon: Sparkles },
    ],
  },
  {
    title: "Explorar",
    items: [
      { key: "favorites", label: "Favoritos", icon: Heart },
      { key: "duplicates", label: "Números Duplicados", icon: Copy },
      { key: "comments", label: "Comentários", icon: MessageCircle },
    ],
  },
];

function daysLeft(iso) {
  if (!iso) return 0;
  return Math.max(0, Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000));
}

export default function Sidebar({ view, setView, alertCount = 0, mobileOpen = false, onClose = () => {}, onOpenSettings = () => {}, onOpenPassword = () => {} }) {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const left = daysLeft(user?.access_expires_at);
  const planLabel = isAdmin ? "Administrador"
    : user?.trial ? `Teste · ${left}d`
    : user?.plan === "all" ? "Todas as cidades"
    : user?.allowed_cities?.length ? `${user.allowed_cities.length} cidade(s)`
    : "Subscrição ativa";

  const go = (key) => { setView(key); onClose(); };

  return (
    <>
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div className="fixed inset-0 bg-black/60 z-30 lg:hidden" onClick={onClose} data-testid="sidebar-backdrop" />
      )}
      <aside
        className={`w-60 shrink-0 border-r border-[#27272a] bg-[#09090b] flex flex-col z-40
          fixed inset-y-0 left-0 overflow-y-auto transform transition-transform duration-200
          lg:static lg:translate-x-0 lg:min-h-screen
          ${mobileOpen ? "translate-x-0" : "-translate-x-full"}`}
        data-testid="sidebar"
      >
      <div className="px-6 py-6 border-b border-[#27272a]">
        <img src="/radarx-logo.png" alt="RadarX" className="h-10 w-auto" />
        <div className="label-tiny mt-2">Painel de Dados</div>
      </div>

      <nav className="flex-1 py-3">
        {(() => {
          const navButton = (n) => {
            const Icon = n.icon;
            const active = view === n.key;
            return (
              <button
                key={n.key}
                data-testid={`nav-${n.key}`}
                onClick={() => go(n.key)}
                className={`w-full flex items-center gap-3 px-6 py-2.5 text-sm transition-colors border-l-2 ${
                  active
                    ? "border-[#c8aa6e] text-[#fafafa] bg-[#18181b]"
                    : "border-transparent text-[#a1a1aa] hover:text-[#fafafa] hover:bg-[#18181b]/50"
                }`}
              >
                <Icon className="w-4 h-4" strokeWidth={1.5} />
                {n.label}
                {n.key === "alerts" && alertCount > 0 && (
                  <span className="ml-auto text-[10px] font-mono bg-[#c8aa6e] text-black px-1.5 py-0.5 rounded-full leading-none" data-testid="alerts-badge">
                    {alertCount > 99 ? "99+" : alertCount}
                  </span>
                )}
              </button>
            );
          };

          return (
            <>
              {SECTIONS.map((section) => {
                const items = section.items.filter((n) => !n.adminOnly || isAdmin);
                if (items.length === 0) return null;
                return (
                  <div key={section.title} className="mb-1.5">
                    <div className="px-6 pt-2 pb-1 label-tiny text-[#52525b]">{section.title}</div>
                    {items.map((n) => navButton(n))}
                  </div>
                );
              })}

              {/* Definições / conta */}
              <div className="mb-1.5">
                <div className="px-6 pt-2 pb-1 label-tiny text-[#52525b]">Definições</div>
                {isAdmin && navButton({ key: "sources", label: "Origens", icon: Globe })}
                <button data-testid="nav-wa-settings" onClick={() => { onClose(); onOpenSettings(); }}
                  className="w-full flex items-center gap-3 px-6 py-2.5 text-sm transition-colors border-l-2 border-transparent text-[#a1a1aa] hover:text-[#fafafa] hover:bg-[#18181b]/50">
                  <MessageCircle className="w-4 h-4" strokeWidth={1.5} /> Mensagem WhatsApp
                </button>
                {isAdmin && (
                  <button data-testid="nav-admin" onClick={() => { onClose(); navigate("/admin"); }}
                    className="w-full flex items-center gap-3 px-6 py-2.5 text-sm transition-colors border-l-2 border-transparent text-[#c8aa6e] hover:bg-[#18181b]/50">
                    <Settings className="w-4 h-4" strokeWidth={1.5} /> Painel Admin
                  </button>
                )}
              </div>
            </>
          );
        })()}
      </nav>

      {/* Subscription card */}
      {!isAdmin && (
        <div className="mx-4 mb-3 border border-[#27272a] bg-[#18181b] p-3" data-testid="subscription-card">
          <div className="flex items-center gap-2 mb-1.5">
            <Crown className="w-3.5 h-3.5 text-[#c8aa6e]" />
            <span className="text-xs text-[#fafafa]">{planLabel}</span>
          </div>
          {user?.allowed_cities?.length > 0 && (
            <div className="flex items-center gap-1 text-[10px] text-[#71717a] mb-2">
              <MapPin className="w-3 h-3" /> {user.allowed_cities.join(", ")}
            </div>
          )}
          <button onClick={() => navigate("/pricing")} data-testid="manage-subscription"
            className="w-full text-[11px] bg-[#c8aa6e] text-black hover:bg-[#d4b982] py-1.5 transition-colors">
            {user?.trial ? "Subscrever agora" : "Gerir / mudar plano"}
          </button>
        </div>
      )}

      <div className="border-t border-[#27272a] p-4">
        <div className="text-xs text-[#71717a] px-2 mb-2 truncate">{user?.email}</div>
        <button
          data-testid="change-password-btn"
          onClick={() => { onClose(); onOpenPassword(); }}
          className="w-full flex items-center gap-2 px-2 py-2 text-sm text-[#a1a1aa] hover:text-[#fafafa] transition-colors"
        >
          <KeyRound className="w-4 h-4" strokeWidth={1.5} /> Alterar palavra-passe
        </button>
        <button
          data-testid="logout-btn"
          onClick={logout}
          className="w-full flex items-center gap-2 px-2 py-2 text-sm text-[#a1a1aa] hover:text-[#ef4444] transition-colors"
        >
          <LogOut className="w-4 h-4" strokeWidth={1.5} /> Terminar Sessão
        </button>
      </div>
      </aside>
    </>
  );
}
